import { logger } from '@/utils/logger';
import api from '@shared/services/api';

/**
 * Conversation namespace for chat_type-scoped active-conversation resolution.
 * Home rides the shared engine with a per-list namespace (`home_reading` /
 * `home_watching`) matching the BE `ChatTypeParam` bound — so reading and
 * watching keep distinct persisted threads. Keep this set in sync with the BE.
 */
export type ChatType = 'general' | 'meals' | 'home_reading' | 'home_watching';

// Types of messages
export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  /** System messages for behind-the-scenes activity (tool calls, thinking, processing, etc.) */
  SYSTEM = 'system',
}

interface Message {
  role: MessageRole;
  content: string | Record<string, unknown>; // Allow complex content structures from API
}

interface Conversation {
  id: string;
  title: string;
  date?: string;
  lastMessageAt?: string;
  isActive: boolean;
  isStarred?: boolean;
  messages: Message[];
  /**
   * The LLM model bound to this conversation. `null` for "Auto" routing,
   * `undefined` if the field was not present in the response.
   */
  modelId?: string | null;
}

interface ServerActiveConversation {
  id: string;
  title: string;
  date?: string;
  lastMessageAt?: string;
  isActive: boolean;
  isStarred?: boolean;
  messages: Message[];
  /** Server uses snake_case; we re-key to camelCase below. */
  model_id?: string | null;
}

interface ConversationResponse {
  success: boolean;
  data?: ServerActiveConversation;
  error?: string;
}

/** Re-key the server payload's snake_case `model_id` onto the camelCase shape. */
function adaptServerConversation(server: ServerActiveConversation): Conversation {
  const { model_id, ...rest } = server;
  return {
    ...rest,
    ...(model_id !== undefined && { modelId: model_id }),
  };
}

// Event listener for new conversation
type NewConversationListener = () => void;
const newConversationListeners: NewConversationListener[] = [];

export const onNewConversation = (listener: NewConversationListener): (() => void) => {
  newConversationListeners.push(listener);
  return () => {
    const index = newConversationListeners.indexOf(listener);
    if (index > -1) {
      newConversationListeners.splice(index, 1);
    }
  };
};

const notifyNewConversation = (): void => {
  newConversationListeners.forEach(listener => listener());
};

// Chat service for communicating with the backend
// NOTE: Chat messaging is now handled via WebSocket (see websocketService.ts)
// This service only provides conversation management endpoints
const ChatService = {
  // Get the active conversation for the current user
  async getActiveConversation(chatType?: ChatType): Promise<Conversation | null> {
    try {
      const qs = chatType ? `?chat_type=${chatType}` : '';
      const response = await api.get<ConversationResponse>(`/api/conversations/active${qs}`);
      if (response.data && response.data.success && response.data.data) {
        return adaptServerConversation(response.data.data);
      }
      return null;
    } catch (error) {
      logger.error('Error getting active conversation:', error);

      return null;
    }
  },

  // Create a new conversation
  async createNewConversation(chatType?: ChatType): Promise<Conversation | null> {
    try {
      const qs = chatType ? `?chat_type=${chatType}` : '';
      const response = await api.post<ConversationResponse>(`/api/conversations/new${qs}`, {});
      if (response.data && response.data.success && response.data.data) {
        notifyNewConversation();
        return response.data.data;
      }
      return null;
    } catch (error) {
      logger.error('Error creating new conversation:', error);

      return null;
    }
  },
};

export default ChatService;
